import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { HttpHelpService } from '../../../Data/service/http-help.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';

/**
 * @Component DocumentHeadComponent
 * @description Manages the header section of a document form, including company, customer, date,
 * and other relevant details like VAT type, language, and currency.
 * Handles sequence checking and creation prompts.
 */
@Component({
	selector: 'app-document-head',
	templateUrl: './document-head.component.html',
	styleUrls: ['./document-head.component.scss'],
	providers: [MessageService]
})
export class DocumentHeadComponent implements OnInit, OnDestroy, OnChanges {
	/** @description The parent FormGroup containing the head controls. */
	@Input() headFormGroup: FormGroup;
	/** @description The type of the document being created/edited (e.g., '305', '320'). */
	@Input() documentType: string;
	/** @description The current mode ('create' or 'edit'). */
	@Input() mode: 'create' | 'edit' = 'create';
	/** @description Flag to enable debug logging. */
	@Input() debug: boolean;
	/** @description List of available languages for the document. */
	@Input() availableLanguages: any[] = [];
	/** @description Flag indicating if the parent form has been submitted. */
	@Input() submitted: boolean = false;
	/** @description Flag indicating if the company select should be disabled. */
	@Input() disableCompanySelect: boolean = false;

	/** @description Emits when the selected company changes. */
	@Output() companyChange = new EventEmitter<number | null>();
	/** @description Emits when the selected customer changes. */
	@Output() customerChange = new EventEmitter<number | null>();
	/** @description Emits when the selected date changes. */
	@Output() dateChange = new EventEmitter<string | null>();
	/** @description Emits when the selected VAT type changes. */
	@Output() vatTypeChange = new EventEmitter<number | null>();
	/** @description Emits when the selected document language changes. */
	@Output() languageChange = new EventEmitter<string | null>();
	/** @description Emits when the selected currency changes. */
	@Output() currencyChange = new EventEmitter<string | null>();

	/** @description The current application language, retrieved from local storage or defaults to 'en'. */
	curentLang: string = localStorage.getItem('curentLang') || 'en';

	/** @description Controls the visibility of the sequence creation dialog. */
	createSequenceDialogVisible: boolean = false;
	/** @description The starting number for a new sequence, bound to the dialog input. */
	sequenceStartNumber: number | null = null;
	/** @description The company ID for which a sequence needs to be created. */
	sequenceCompanyId: number | null = null;
	/** @description The document type code for which a sequence needs to be created. */
	sequenceDocType: string | null = null;

	/** @description Indicates whether a sequence exists for the current company and document type. Null if not checked or not applicable. */
	sequenceExists: boolean | null = null;
	/** @description Flag to prevent concurrent sequence check requests. */
	isLoadingSequence: boolean = false;

	/** @description Controls the visibility of the additional info section (VAT, Language, Currency). */
	showAdditionalInfo: boolean = false;

	/** @description Current company ID for passing to customer select component. */
	currentCompanyId: number | null = null;

	/** @description Subscription to company_id form control value changes. */
	private companyIdSubscription?: Subscription;

	/**
	 * @constructor
	 * @param {NgxSpinnerService} spinner Service for displaying loading spinners.
	 * @param {HttpHelpService} service Service for making HTTP requests (data operations).
	 * @param {TranslateService} translate Service for handling translations.
	 * @param {MessageService} messageService Service for displaying messages (toasts).
	 */
	constructor(
		private spinner: NgxSpinnerService,
		private service: HttpHelpService,
		private translate: TranslateService,
		private messageService: MessageService
	) {}

	/**
	 * @description Lifecycle hook called after component initialization.
	 * Performs initial setup like VAT control validation.
	 */
	ngOnInit(): void {
		this.updateVatControlValidation();
		const companyId = this.headFormGroup?.get('company_id')?.value;
		this.currentCompanyId = companyId;
		
		// Subscribe to company_id changes to update currentCompanyId
		const companyIdControl = this.headFormGroup?.get('company_id');
		if (companyIdControl) {
			this.companyIdSubscription = companyIdControl.valueChanges.subscribe((newCompanyId: number | null) => {
				this.currentCompanyId = newCompanyId;
			});
		}
		
		if (companyId && this.documentType) {
			this.checkSequenceExists(companyId, this.documentType);
		}
	}

	/**
	 * @description Lifecycle hook called when any data-bound property of a directive changes.
	 * Handles changes to the documentType input, updating VAT validation and checking sequence existence.
	 * @param {SimpleChanges} changes Object containing the changed properties.
	 */
	ngOnChanges(changes: SimpleChanges): void {
		if (changes['documentType']) {
			const currentDocType = changes['documentType'].currentValue;
			const previousDocType = changes['documentType'].previousValue;
			const isFirstChange = changes['documentType'].firstChange;

this.updateVatControlValidation();

			const currentCompanyId = this.headFormGroup.get('company_id')?.value;
			if (!isFirstChange && currentCompanyId && currentDocType) {
				this.checkSequenceExists(currentCompanyId, currentDocType);
			}
		}
	}

	/**
	 * @description Lifecycle hook that is called when a directive, pipe, or service is destroyed.
	 * Used for cleanup.
	 */
	ngOnDestroy(): void {
		// Unsubscribe from company_id changes
		if (this.companyIdSubscription) {
			this.companyIdSubscription.unsubscribe();
		}
	}

	/**
	 * @description Forwards the company change event from the child component.
	 * Triggers closed month check and sequence existence check.
	 * @param {number | null} companyId The selected company ID, or null if cleared.
	 */
	forwardCompanyChange(companyId: number | null): void {
// Update currentCompanyId immediately to trigger customer reload
		this.currentCompanyId = companyId;
		
		this.companyChange.emit(companyId);
		this.checkClosedMonth();
		if (companyId !== null) {
			this.checkSequenceExists(companyId, this.documentType || '');
		} else {
			this.sequenceExists = null;
		}
	}

	/**
	 * @description Forwards the customer change event from the child component.
	 * @param {number | null} customerId The selected customer ID, or null if cleared.
	 */
	forwardCustomerChange(customerId: number | null): void {
this.customerChange.emit(customerId);
	}

	/**
	 * @description Forwards the date change event from the child component.
	 * Triggers closed month check.
	 * @param {string | null} date The selected date string, or null if cleared.
	 */
	forwardDateChange(date: string | null): void {
this.dateChange.emit(date);
		this.checkClosedMonth();
	}

	/**
	 * @description Forwards the VAT type change event from the child component.
	 * @param {number | null} selectedId The selected VAT list ID, or null if cleared.
	 */
	forwardVatTypeChange(selectedId: number | null) {
this.vatTypeChange.emit(selectedId);
	}

	/**
	 * @description Forwards the language change event from the child component.
	 * @param {string | null} languageCode The selected language code, or null if cleared.
	 */
	forwardLanguageChange(languageCode: string | null): void {
this.languageChange.emit(languageCode);
	}

	/**
	 * @description Forwards the currency change event from the child component.
	 * @param {string | null} currencyCode The selected currency code, or null if cleared.
	 */
	forwardCurrencyChange(currencyCode: string | null): void {
this.currencyChange.emit(currencyCode);
	}

	/**
	 * @description Checks if the selected month/year for the chosen company is closed for accounting.
	 * Displays a warning message if it is closed. Uses the `company_id` and `invoice_date` from the `headFormGroup`.
	 */
	checkClosedMonth(): void {
		const companyId = this.headFormGroup.get('company_id')?.value;
		const invoiceDateStr = this.headFormGroup.get('invoice_date')?.value;

		if (!companyId || !invoiceDateStr) {
return;
		}

		const selectedDate = new Date(invoiceDateStr);
		const month = selectedDate.getMonth() + 1;
		const year = selectedDate.getFullYear();

this.spinner.show();
		this.service.checkClosedMonth(companyId, month, year).subscribe({
			next: (res: { status: number }) => {
				if (res.status === 200) {
this.messageService.add({
						severity: 'error',
						summary: this.translate.instant('notifications.warningSummary'),
						detail: this.translate.instant('notifications.closedMonthWarning')
					});
				} else {
}
				this.spinner.hide();
			},
			error: (error) => {
				console.error('[DEBUG/DocumentComponent] Error checking closed month:', error);
				this.messageService.add({
					severity: 'error',
					summary: this.translate.instant('notifications.errorSummary'),
					detail: this.translate.instant('notifications.closedMonthCheckError')
				});
				this.spinner.hide();
			},
		});
	}

	/**
	 * @description Checks if a document sequence exists for the given company and document type via the service.
	 * If it doesn't exist, prompts the user to create one using `promptCreateSequence`.
	 * Updates the `sequenceExists` flag. Handles loading state and errors.
	 * @param {number} companyId The ID of the company.
	 * @param {string} docType The document type code.
	 */
	checkSequenceExists(companyId: number, docType: string): void {
		if (companyId === null || !docType) {
			if (this.debug) {
				console.warn(`[DEBUG/DocHead] Sequence check skipped. Company ID: ${companyId}, Mapped DocType: ${docType}`);
			}
			this.sequenceExists = null;
			return;
		}

		if (this.isLoadingSequence) return;
		this.isLoadingSequence = true;
		this.sequenceExists = null;
		this.spinner.show();
this.service.checkSequenceExists(companyId, docType).subscribe(
			(exists: boolean) => {
				this.sequenceExists = exists;
this.isLoadingSequence = false;
				this.spinner.hide();
				if (!this.sequenceExists) {
					this.promptCreateSequence(companyId, docType);
				}
			},
			(error) => {
				console.error(`[DEBUG/DocHead] Error checking sequence for company ${companyId}, sequenceType ${docType}:`, error);
				this.messageService.add({
					severity: 'error',
					summary: this.translate.instant('notifications.errorSummary'),
					detail: this.translate.instant('modal.documentSequence.checkError')
				});
				this.sequenceExists = null;
				this.isLoadingSequence = false;
				this.spinner.hide();
			}
		);
	}

	/**
	 * @description Opens the dialog to prompt the user for creating a new sequence.
	 * Sets component properties needed for the dialog (`sequenceCompanyId`, `sequenceDocType`, `sequenceStartNumber`).
	 * @param {number} companyId The company ID for the sequence.
	 * @param {string} docType The document type code for the sequence.
	 */
	promptCreateSequence(companyId: number, docType: string): void {
		this.sequenceCompanyId = companyId;
		this.sequenceDocType = docType;
		this.sequenceStartNumber = this.getDefaultSequenceNumber(docType);
		this.createSequenceDialogVisible = true;
}

	/**
	 * @description Handles the confirmation action from the sequence creation dialog.
	 * Validates the `sequenceStartNumber` input. If valid, calls the `createSequence` method.
	 * Closes the dialog regardless of success or failure of the creation call.
	 */
	confirmCreateSequence(): void {
		const startNumber = this.sequenceStartNumber;
		const validationErrorKey = 'modal.documentSequence.promptValidationError';
		const validationErrorDefault = 'Start number must be a positive integer.';

		if (startNumber === null || isNaN(startNumber) || !Number.isInteger(startNumber) || startNumber <= 0) {
			this.messageService.add({
				severity: 'error',
				summary: this.translate.instant('notifications.errorSummary'),
				detail: this.translate.instant(validationErrorKey, validationErrorDefault)
			});
			return;
		}

		if (this.sequenceCompanyId !== null && this.sequenceDocType !== null) {
			this.createSequence(this.sequenceCompanyId, this.sequenceDocType, startNumber);
		} else {
			console.error("[DEBUG/DocHead] Missing companyId or sequenceDocType for sequence creation");
			this.messageService.add({
				severity: 'error',
				summary: this.translate.instant('notifications.errorSummary'),
				detail: this.translate.instant('errors.unexpected')
			});
		}

		this.createSequenceDialogVisible = false;
	}

	/**
	 * @description Handles the cancellation action from the sequence creation dialog.
	 * Closes the dialog and shows a warning message indicating cancellation.
	 */
	cancelCreateSequence(): void {
		this.createSequenceDialogVisible = false;
		const warningMsgKey = 'documentSequenceModal.creationCancelledWarning';
		const warningMsgDefault = 'Sequence creation cancelled. Document cannot be confirmed without a sequence.';
		this.messageService.add({
			severity: 'warn',
			summary: this.translate.instant('notifications.warningSummary'),
			detail: this.translate.instant(warningMsgKey, warningMsgDefault)
		});
}

	/**
	 * @description Calls the service to create a new document sequence with the provided details.
	 * Updates the `sequenceExists` flag to true on success and displays success/error messages.
	 * @param {number} companyId The company ID.
	 * @param {string} docType The document type code.
	 * @param {number} startWith The starting number for the sequence.
	 */
	createSequence(companyId: number, docType: string, startWith: number): void {
		this.spinner.show();
		this.service.createSequence(companyId, docType, startWith).subscribe({
			next: () => {
				this.spinner.hide();
				this.sequenceExists = true;
				const successMsg = this.translate.instant('modal.documentSequence.creationSuccess', { startNumber: startWith });
				this.messageService.add({
					severity: 'success',
					summary: this.translate.instant('notifications.successSummary'),
					detail: successMsg
				});
},
			error: (error) => {
				this.spinner.hide();
				console.error(`[DEBUG/DocHead] Error creating sequence for company ${companyId}, sequenceType ${docType}:`, error);
				const errorMsgKey = 'modal.documentSequence.creationError';
				const errorMsgDefault = 'Failed to create sequence.';
				this.messageService.add({
					severity: 'error',
					summary: this.translate.instant('notifications.errorSummary'),
					detail: this.translate.instant(errorMsgKey, errorMsgDefault)
				});
			}
		});
	}

	/**
	 * @description Determines if the VAT Type selection is relevant for the current document type.
	 * Relevant only if type is not '400'.
	 * @param {string | null} docType The document type code.
	 * @returns {boolean} True if VAT Type is not relevant (docType is '400'), false otherwise.
	 */
	isVatTypeRelevant(docType: string | null): boolean {
		return docType !== '400';
	}

	/**
	 * @description Updates the validation status (required or not) of the `vat_list_id` form control
	 * within the `headFormGroup` based on the current `documentType`'s relevance determined by `isVatTypeRelevant`.
	 * If not relevant, it also clears the control's value.
	 * @private
	 */
	private updateVatControlValidation(): void {
		const vatControl = this.headFormGroup?.get('vat_list_id');
		if (vatControl) {
			if (this.isVatTypeRelevant(this.documentType)) {
				vatControl.setValidators(Validators.required);
} else {
				vatControl.clearValidators();
				if (vatControl.value !== null) {
					vatControl.setValue(null, { emitEvent: false });
				}
}
			vatControl.updateValueAndValidity({ emitEvent: false });
		} else if (this.debug) {
			 console.warn('[DEBUG/DocHead] Could not find vat_list_id control in headFormGroup to update validation.');
		}
	}

	/**
	 * @description Returns the default starting number for a sequence based on document type.
	 * @param {string} docType The document type code.
	 * @returns {number} The default starting number for the sequence.
	 */
	private getDefaultSequenceNumber(docType: string): number {
		const defaultNumbers: { [key: string]: number } = {
			'305': 10001,  // Tax Invoice
			'320': 40001,  // Invoice With Receipt
			'332': 50001,  // Financial Request
			'400': 30001,  // Receipt
			'330': 20001,  // Reverse Invoice
			'430': 60001,  // Reverse Receipt
			'100': 70001,  // Order
			'200': 80001,  // Mission
			'0': 90001,    // Offer Price
			'000': 90001   // Offer Price (alternative format)
		};
		
		return defaultNumbers[docType] || 1;
	}
}